import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Sheet, SheetContent, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { LeadConvertAction } from "../../service/leads/lead-convert-action";
import { LeadAttributesBlock } from "../shared/attribute-values/attribute-values-editor";
import { useFormatters } from "../shared/format";
import { LeadActivityTimeline, type NameOf } from "./lead-activity";
import { LeadAvatar } from "./lead-bits";
import { LeadControls } from "./lead-controls";
import { displayMobile, isMaskedMobile, leadInterest } from "./lead-logic";
import { LeadNotes } from "./lead-notes";
import { LeadSummary } from "./lead-summary";
import type { LeadDetail, Stage } from "./lead-types";
import { useLeadDetail } from "./use-leads";

interface LeadSheetProps {
  leadId: string | null;
  onClose: () => void;
  stages: readonly Stage[];
  canEdit: boolean;
  nameOf: NameOf;
}

function Detail({ label, children, mono = false }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold text-fg-subtle">{label}</dt>
      <dd className={mono ? "font-mono" : "truncate"}>{children}</dd>
    </div>
  );
}

function QuickActions({ lead }: { lead: LeadDetail }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canCall = lead.mobile && !isMaskedMobile(lead.mobile);
  return (
    <div className="flex flex-wrap gap-2">
      {canCall ? (
        <Button asChild size="sm">
          <a href={`tel:${lead.mobile ?? ""}`}>
            <Icon name="phone" />
            {t("engage.leads.sheet.call")}
          </a>
        </Button>
      ) : null}
      {lead.email ? (
        <Button asChild size="sm" variant="secondary">
          <a href={`mailto:${lead.email}`}>
            <Icon name="mail" />
            {t("engage.leads.sheet.sendEmail")}
          </a>
        </Button>
      ) : null}
      {lead.conversation_id ? (
        <Button asChild size="sm" variant="ghost">
          <AppLink href={consoleRoutes.inbox(summary.slug, lead.conversation_id)}>
            <Icon name="messages-square" />
            {t("engage.leads.sheet.openConversation")}
          </AppLink>
        </Button>
      ) : null}
      <LeadConvertAction lead={lead} />
    </div>
  );
}

function LeadBody({ lead, stages, canEdit, nameOf }: { lead: LeadDetail } & Omit<LeadSheetProps, "leadId" | "onClose">) {
  const { t } = useTranslation();
  const format = useFormatters();
  const learner = Object.values(lead.learner).filter((v) => typeof v === "string" || typeof v === "number").join(", ");
  const custom = Object.entries(lead.custom_fields).filter(([, v]) => typeof v === "string" && v);
  return (
    <div className="space-y-5">
      <QuickActions lead={lead} />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Detail label={t("engage.leads.sheet.mobile")} mono>
          <span data-testid="sheet-mobile">{displayMobile(lead.mobile) || "—"}</span>
        </Detail>
        <Detail label={t("engage.leads.sheet.email")}>{lead.email ?? "—"}</Detail>
        <Detail label={t("engage.leads.sheet.interest")}>{lead.interest ?? "—"}</Detail>
        {learner ? <Detail label={t("engage.leads.sheet.learner")}>{learner}</Detail> : null}
        <Detail label={t("engage.leads.sheet.preferredContact")}>{lead.preferred_contact ?? "—"}</Detail>
        <Detail label={t("engage.leads.sheet.consent")}>
          {lead.consent_at ? (
            <span className="flex items-center gap-1 text-success">
              <Icon name="check-circle" className="size-3.5" />
              {format.dateTime(lead.consent_at)}
            </span>
          ) : (
            t("engage.leads.sheet.noConsent")
          )}
        </Detail>
        {custom.map(([key, value]) => (
          <Detail key={key} label={key.replace(/_/g, " ")}>
            {String(value)}
          </Detail>
        ))}
      </dl>
      <LeadControls lead={lead} stages={stages} canEdit={canEdit} />
      <LeadAttributesBlock leadId={lead.id} canEdit={canEdit} />
      {canEdit ? null : <p className="text-xs text-fg-subtle">{t("engage.leads.sheet.readOnly")}</p>}
      <LeadSummary lead={lead} canEdit={canEdit} />
      <LeadActivityTimeline activities={lead.activities} nameOf={nameOf} />
      <LeadNotes leadId={lead.id} activities={lead.activities} nameOf={nameOf} canEdit={canEdit} />
    </div>
  );
}

function SheetSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <Skeleton className="h-8 w-[260px]" />
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
      <Skeleton className="h-28" />
      <Skeleton className="h-32" />
    </div>
  );
}

/** `LeadSheet` at `/leads/:leadId`. */
export function LeadSheet({ leadId, onClose, stages, canEdit, nameOf }: LeadSheetProps) {
  const { t } = useTranslation();
  const detail = useLeadDetail(leadId);
  const lead = detail.data;
  const channel = lead?.source_channel ? t(`engage.channels.${lead.source_channel}`, { defaultValue: lead.source_channel }) : "";
  const title = lead ? (
    <span className="flex items-center gap-3">
      <LeadAvatar name={lead.name} className="size-10 text-sm" />
      <span className="min-w-0">
        <span className="block">{lead.name}</span>
        <span className="block text-xs font-normal text-fg-subtle">{channel ? t("engage.leads.sheet.via", { interest: leadInterest(lead) === "" ? (lead.email ?? "") : leadInterest(lead), channel }) : leadInterest(lead)}</span>
      </span>
    </span>
  ) : (
    t("engage.leads.title")
  );
  return (
    <Sheet
      open={leadId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent title={title} className="max-w-lg" closeLabel={t("common.close")}>
        {detail.isPending ? <SheetSkeleton /> : null}
        {detail.isError ? (
          <ErrorState headingLevel={3} title={t("engage.leads.sheet.loadError")} onRetry={() => void detail.refetch()} retryLabel={t("common.retry")} />
        ) : null}
        {lead ? <LeadBody lead={lead} stages={stages} canEdit={canEdit} nameOf={nameOf} /> : null}
        {!detail.isPending && !detail.isError && !lead ? <EmptyState icon="user" headingLevel={3} title={t("engage.leads.sheet.notFound")} /> : null}
      </SheetContent>
    </Sheet>
  );
}
